import { FetchStatus, QueryStatus, UseQueryResult } from "@tanstack/react-query";
import { Skeleton, SkeletonProps } from "./loaders/Skeleton/Skeleton";
import BrandLoader, { BrandLoaderProps } from "./loaders/Spinner/BrandLoader";
import { Spinner, SpinnerProps } from "./loaders/Spinner/Spinner";
// import { LoadingView } from "./LoadingView";
import { Alert } from "./overlays/Alert/Alert";
import { ErrorContainer, ServerError } from "./helperfunctions/ApiError";
import { CSSProperties } from "react";

// export type LoadingStatus = "loading" | "error" | "success" | "idle";

type Loaders =
  | "spinner"
  | "spinner-inline"
  | "fid"
  | "skeleton-block"
  | "skeleton-pairs"
  | "skeleton-rows"
  | "skeleton-columns"
  | "entity-tile"
  | "filters"
  | "none";
export interface LoadingWrapperProps {
  children: React.ReactNode;
  status: QueryStatus;
  fetchStatus: FetchStatus;
  error?: any;
  type?: Loaders;
  alertStyle?: CSSProperties;
  loaderProps?: Omit<RenderLoaderProps, "type">;
}

interface RenderLoaderProps {
  type: Loaders;
  spinnerProps?: SpinnerProps;
  brandLoaderProps?: BrandLoaderProps;
  skeletonProps?: Omit<SkeletonProps, "type">;
}
export const RenderLoader = ({ type, spinnerProps, brandLoaderProps, skeletonProps }: RenderLoaderProps) => {
  switch (type) {
    case "spinner":
      return <Spinner {...spinnerProps} />;
    case "spinner-inline":
      return <Spinner size="xs" style={{ margin: "0 5px" }} inline {...spinnerProps} />;
    case "fid":
      return <BrandLoader size={100} {...brandLoaderProps} />;
    case "skeleton-block":
      return <span className="skeleton-block" style={{ height: 37 }} />;
    case "entity-tile":
      return <span className="skeleton-block" style={{ height: 54 }} />;
    case "skeleton-pairs":
      return <Skeleton type="pairs" {...skeletonProps} />;
    case "skeleton-rows":
      return <Skeleton type="rows" {...skeletonProps} />;
    case "skeleton-columns":
      return <Skeleton type="columns" {...skeletonProps} />;
    case "filters":
      return <Skeleton type="filters" {...skeletonProps} />;
    case "none":
      return <></>;
    default:
      return <Spinner {...spinnerProps} />;
  }
};

export const LoadingWrapper = ({
  children,
  status,
  fetchStatus,
  error,
  type = "spinner",
  alertStyle,
  loaderProps,
}: LoadingWrapperProps) => {
  // Disabled queries
  // if (status === "loading" && fetchStatus === "idle") return renderLoader(type);
  if (status === "loading" && fetchStatus === "fetching") return <RenderLoader type={type} {...loaderProps} />;
  if (status === "error")
    return (
      <Alert
        type="danger"
        message={
          <>
            {error && Object.hasOwn(error, "message") ? (
              <ErrorContainer message={error.message} status={error.status ?? undefined} />
            ) : error && Object.hasOwn(error, "details") ? (
              <ErrorContainer message={error.details} status={error.status ?? undefined} />
            ) : error && Object.hasOwn(error, "errors") ? (
              <ErrorContainer message={error.errors} status={error.errors ?? undefined} />
            ) : (
              "Connection to server lost"
            )}
          </>
        }
        children={
          error && Object.hasOwn(error, "details") ? (
            <div className="flex col-nowrap gap-5">
              <label>Stacktrace:</label>
              <code>{(error as ServerError).details}</code>
            </div>
          ) : undefined
        }
        fit
        centered
        style={{ ...alertStyle }}
      />
    );
  // if (error && error.hasOwnProperty("message")) console.error(error.message);
  return <>{children}</>;
};

type ArrayOfReactQueryResponses = UseQueryResult[];

export const getLoadingState = (
  responses: ArrayOfReactQueryResponses
): {
  status: QueryStatus;
  fetchStatus: FetchStatus;
  error: any;
} => {
  let status: QueryStatus = "loading";
  let fetchStatus: FetchStatus = "idle";
  const isLoading = responses.some((response) => response.status === "loading");
  const isError = responses.some((response) => response.status === "error");
  const isSuccess = responses.every((response) => response.status === "success");
  const isfetching = responses.some((response) => response.fetchStatus === "fetching");
  if (isError) status = "error"; // if any query has an error, the combined state is always an error
  if (isLoading) status = "loading"; // if there are no errors and any query is still loading, combined state is loading
  if (isSuccess) status = "success";
  if (isfetching) fetchStatus = "fetching";
  const error = responses.reduce((acc, response) => ({ ...acc, ...((response.error as any) ?? ({} as any)) }), {});

  return { status, fetchStatus, error };
};
